import { ReservationNode } from '@/api/apis/resources';
import {
  BaseResource,
  Hub,
  Region,
  ResolveHrSystemConflict,
  ResourceShiftTypeHrSystemConflict,
  ShiftType,
} from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';

export enum SchedulerViewType {
  Scheduler = 'scheduler',
  Reservations = 'reservations',
}

export enum ScheduleStatusMode {
  PrePlanning = 'PrePlanning',
  Planning = 'Planning',
  Published = 'Published',
}

/**
 * Marks a temporal boundary between a shift and the previous-day shift caused by a time-group
 * transition (CS only). `Gap` = A→B (8h uncovered), `Overlap` = B→A (8h double-covered).
 * Owned by the later shift; colors/opacity are resolved in the render layer.
 */
export enum ShiftEdgeKind {
  Gap = 'gap',
  Overlap = 'overlap',
}

type BaseShiftSiblingData = {
  hasPrev?: boolean;
  hasNext?: boolean;
  hasPartialPrev?: boolean;
  hasPartialNext?: boolean;
  prevEdge?: ShiftEdgeKind;
  isExternal?: false;
};

export type ShiftSiblingData =
  | BaseShiftSiblingData
  | (Omit<BaseShiftSiblingData, 'isExternal'> & { isExternal: true; id: string; name: string });

export type SchedulerMutationActions = {
  isMutating: boolean;
  onPlanningStart: () => Promise<void>;
  onGenerationStart: () => Promise<void>;
  onPlanningDiscard: () => Promise<void>;
  onPlanningPublish: () => Promise<void>;
  onGenerationCancel: () => Promise<void>;
};

export type SchedulerDayColumn = {
  key: string;
  date: Date;
  label: string;
  isToday: boolean;
  isPast: boolean;
  isSelectedDay: boolean;
  isCurrentMonth: boolean;
};

export type HubsDataDictionary = Record<string, Hub>;

export type RegionsDataDictionary = Record<string, Region>;

type ResourceShiftTypeCount<T extends string> = Partial<Record<T, number>>;
export type ResourceShiftTotalsMap<T extends string> = Record<string, ResourceShiftTypeCount<T>>;

export type WorkdaySyncData = {
  isDisabled: boolean;
  lastSyncTime?: string | null;
  onWorkdaySync: () => void;
};

export type WorkdaySyncHookData<R extends BaseResource, T extends ShiftType, P extends Record<string, unknown>> = {
  isDisabled: boolean;
  lastSyncTime?: string | null;
  onWorkdaySync: () => void;
  isConflictsLoading: boolean;
  workdayConflicts?: ResourceShiftTypeHrSystemConflict<R, T>[];
  onWorkdayConflictResolve: (payload: ResolveHrSystemConflict<P>) => void;
};

export enum ViolationDayType {
  Daily = 'daily',
  Weekly = 'weekly',
}

export type BaseViolationItem = {
  title: string;
  messages: string[];
  id?: string;
  day?: string;
};

export type ServiceViolationItem = BaseViolationItem & {
  id?: string;
  day: string;
  dayType: ViolationDayType;
  // isHard: boolean;
};

export type ResourceViolationItem = BaseViolationItem & {
  id: string;
};

export type SupportViolationItem = BaseViolationItem;

export type ServiceRulesViolations = {
  daily?: Record<string, ServiceViolationItem>;
  weekly?: Record<string, ServiceViolationItem>;
};

export type ViolationHighlightData = {
  resourceId?: string;
  hubId?: string;
  day?: Date;
};

export type ResourceReservationsToDayMapsType = Record<string, Record<string, ReservationNode[]>>;

export type SchedulerResource = BaseResource & {
  organs?: OrganType[] | null;
  phoneSupportOrgans?: OrganType[] | null;
  shadowOrgans?: OrganType[] | null;
  proctorOrgans?: OrganType[] | null;
  canPhoneSupport?: boolean;
};
